export const MAX_TRIP_EXPENSES = 1000;

export type TripStyle =
	| "food"
	| "culture"
	| "nature"
	| "shopping"
	| "family"
	| "slow";
export type TripStage =
	| "planning"
	| "upcoming"
	| "traveling"
	| "completed"
	| "archived";
export type TripPlanItemType = "spot" | "food" | "transport" | "memo";
export type TripStopRole = "required" | "optional" | "meal" | "rest";

export type TripPlanItem = {
	id: string;
	type: TripPlanItemType;
	title: string;
	area: string;
	time: string | null;
	estimateJpy: number | null;
	mapQuery: string | null;
	note: string | null;
	completed: boolean;
	placeId?: string;
	courseId?: string;
	role?: TripStopRole;
	setting?: "indoor" | "covered" | "outdoor" | "unknown";
	japaneseName?: string;
	sourceUrl?: string | null;
	checkedAt?: string | null;
	selectionReason?: string;
	stayMinutes?: number;
	locked?: boolean;
	deferred?: boolean;
	returnReason?: string;
};

/** Content stop identity belongs in placeId; each appended itinerary item gets its own id. */
export type TripStopInput = Omit<TripPlanItem, "id" | "completed"> & {
	id?: string;
	completed?: boolean;
};
export type TripAdjustmentCondition = "rain" | "tired" | "short_time";
export type TripDayAdjustmentPreview = {
	tripId: string;
	date: string;
	condition: TripAdjustmentCondition;
	beforeItems: TripPlanItem[];
	afterItems: TripPlanItem[];
	changes: { itemId: string; title: string; reason: string }[];
	baseFingerprint: string;
};
export type TripDayAdjustment = TripDayAdjustmentPreview & {
	afterFingerprint: string;
	appliedAt: string;
};
export type TripStopReplacementPreview = {
	tripId: string;
	date: string;
	sourceItemId: string;
	beforeItems: TripPlanItem[];
	afterItems: TripPlanItem[];
	replacementItem: TripPlanItem;
	baseFingerprint: string;
};
export type TripStopReplacement = TripStopReplacementPreview & {
	afterFingerprint: string;
	appliedAt: string;
};
export type TripCompanionNote = {
	id: string;
	label: string;
	mustVisit: string;
	meetingNote: string;
};

export type TripDay = { date: string; items: TripPlanItem[] };
export type TripChecklistItem = {
	id: string;
	title: string;
	group: "entry" | "money" | "connectivity" | "route" | "personal";
	done: boolean;
	officialUrl: string | null;
	assignee?: string;
	dueOffsetDays?: number;
};
export type TripExpense = {
	id: string;
	title: string;
	category: "transport" | "food" | "stay" | "shopping" | "experience" | "other";
	amountJpy: number;
	occurredOn: string;
	paidBy?: string;
	splitWith?: string[];
	currency?: "JPY" | "KRW";
	amountKrw?: number;
};
export type PersonalFareNote = {
	id: string;
	amountKrw: number;
	departureDate: string;
	observedOn: string;
	seller: string;
	baggage: string;
};

export type TripFilmMoment = {
	id: string;
	planItemId: string | null;
	dayDate: string;
	placeTitle: string;
	neighborhood: string;
	capturedAt: string;
	photoDataUrl: string | null;
	photoMimeType: "image/jpeg" | "image/png" | "image/webp" | null;
	featured: boolean;
	createdAt: string;
	note?: string;
};

export type TripWorkspace = {
	party?: { id: string; nickname: string }[];
	companionType?: "baby" | "parents" | "friends" | "other";
	stay?: { name: string; addressJa: string; note: string };
	returnTransport?: { minutes: number; buffer: number };
	calendarAddedAt?: string;
	shareId?: string;
	shareRevision?: number;
	sharedFrom?: { shareId: string; revision: number; receivedAt: string };
	votes?: Record<string, { nick: string; value: "yes" | "skip"; at: string }[]>;
	settlements?: {
		from: string;
		to: string;
		amountJpy: number;
		doneAt: string;
	}[];
	bookTheme?: string;
	schemaVersion: 1;
	id: string;
	title: string;
	origin: string;
	destination: string;
	destinationCity: string;
	startDate: string;
	endDate: string;
	partySize: number;
	styles: TripStyle[];
	dailyBudgetJpy: number;
	krwPer100Jpy: number | null;
	stageOverride: "archived" | null;
	days: TripDay[];
	checklist: TripChecklistItem[];
	expenses: TripExpense[];
	fareNotes: PersonalFareNote[];
	/** Additive v1 field. Older local trips may not have it until their next save. */
	filmMoments?: TripFilmMoment[];
	/** Undated places retained when a new trip is shorter or has not been scheduled yet. */
	candidates?: TripPlanItem[];
	companionNotes?: TripCompanionNote[];
	/** User-entered local planning boundaries, never an inferred flight or booking. */
	travelContext?: {
		stayArea: string;
		arrivalTime: string | null;
		departureTime: string | null;
	};
	lastAdjustment?: TripDayAdjustment | null;
	lastReplacement?: TripStopReplacement | null;
	createdAt: string;
	updatedAt: string;
};

export type CreateTripInput = Pick<
	TripWorkspace,
	| "title"
	| "origin"
	| "destination"
	| "destinationCity"
	| "startDate"
	| "endDate"
	| "partySize"
	| "styles"
	| "dailyBudgetJpy"
> & { krwPer100Jpy?: number | null };

const isoDatePattern = /^\d{4}-\d{2}-\d{2}$/;

export function dateRange(startDate: string, endDate: string) {
	if (!isoDatePattern.test(startDate) || !isoDatePattern.test(endDate))
		throw new Error("INVALID_TRIP_DATE");
	const start = new Date(`${startDate}T00:00:00Z`);
	const end = new Date(`${endDate}T00:00:00Z`);
	if (
		!Number.isFinite(start.getTime()) ||
		!Number.isFinite(end.getTime()) ||
		start.toISOString().slice(0, 10) !== startDate ||
		end.toISOString().slice(0, 10) !== endDate
	)
		throw new Error("INVALID_TRIP_DATE");
	const length = Math.floor((end.getTime() - start.getTime()) / 86_400_000) + 1;
	if (!Number.isFinite(length) || length < 1 || length > 31)
		throw new Error("INVALID_TRIP_RANGE");
	return Array.from({ length }, (_, index) =>
		new Date(start.getTime() + index * 86_400_000).toISOString().slice(0, 10),
	);
}

export const defaultTripChecklist = (): TripChecklistItem[] => [
	{
		id: "passport",
		title: "여권 만료일과 영문 이름 확인",
		group: "entry",
		done: false,
		officialUrl: null,
	},
	{
		id: "visit-japan-web",
		title: "Visit Japan Web 입국·세관 준비",
		group: "entry",
		done: false,
		officialUrl: "https://services.digital.go.jp/en/visit-japan-web/",
	},
	{
		id: "insurance",
		title: "여행자보험 보장과 긴급 연락 경로 확인",
		group: "entry",
		done: false,
		officialUrl: "https://www.japan.travel/en/plan/travel-insurance-in-japan/",
	},
	{
		id: "payment",
		title: "해외 결제 카드와 소액 현금 준비",
		group: "money",
		done: false,
		officialUrl: null,
	},
	{
		id: "connectivity",
		title: "로밍·eSIM·Wi-Fi 중 하나 준비",
		group: "connectivity",
		done: false,
		officialUrl: null,
	},
	{
		id: "offline",
		title: "오늘 모드와 필요한 지도 영역을 출발 전에 열기",
		group: "route",
		done: false,
		officialUrl: "https://support.google.com/maps/answer/6291838?hl=ko",
	},
	{
		id: "route-refresh",
		title: "출발 72시간 안에 항공편과 공항 첫 길 재확인",
		group: "route",
		done: false,
		officialUrl: null,
	},
];

const cleanText = (value: string, limit: number) =>
	value.trim().replace(/\s+/g, " ").slice(0, limit);
const finiteInteger = (value: number, min: number, max: number) =>
	Number.isInteger(value) && value >= min && value <= max;

export function createTripWorkspace(
	input: CreateTripInput,
	nowIso: string,
	id: string = crypto.randomUUID(),
): TripWorkspace {
	const days = dateRange(input.startDate, input.endDate).map((date) => ({
		date,
		items: [],
	}));
	if (
		!cleanText(input.title, 80) ||
		!/^[A-Z]{3}$/.test(input.origin) ||
		!/^[A-Z]{3}$/.test(input.destination)
	)
		throw new Error("INVALID_TRIP_IDENTITY");
	if (
		!finiteInteger(input.partySize, 1, 12) ||
		!finiteInteger(input.dailyBudgetJpy, 0, 1_000_000)
	)
		throw new Error("INVALID_TRIP_BUDGET");
	if (
		input.krwPer100Jpy != null &&
		!finiteInteger(input.krwPer100Jpy, 100, 10_000)
	)
		throw new Error("INVALID_EXCHANGE_RATE");
	const result: TripWorkspace = {
		schemaVersion: 1,
		id,
		title: cleanText(input.title, 80),
		origin: input.origin,
		destination: input.destination,
		destinationCity: cleanText(input.destinationCity, 40),
		startDate: input.startDate,
		endDate: input.endDate,
		partySize: input.partySize,
		styles: [...new Set(input.styles)].slice(0, 6),
		dailyBudgetJpy: input.dailyBudgetJpy,
		krwPer100Jpy: input.krwPer100Jpy ?? null,
		stageOverride: null,
		days,
		checklist: defaultTripChecklist(),
		expenses: [],
		fareNotes: [],
		filmMoments: [],
		createdAt: nowIso,
		updatedAt: nowIso,
	};
	if (!isTripWorkspace(result)) throw new Error("INVALID_TRIP_WORKSPACE");
	return result;
}

export function tripStage(trip: TripWorkspace, today: string): TripStage {
	if (trip.stageOverride === "archived") return "archived";
	if (today < trip.startDate) {
		const daysUntil = Math.ceil(
			(Date.parse(`${trip.startDate}T00:00:00Z`) -
				Date.parse(`${today}T00:00:00Z`)) /
				86_400_000,
		);
		return daysUntil <= 14 ? "upcoming" : "planning";
	}
	if (today <= trip.endDate) return "traveling";
	return "completed";
}

export function tripProgress(trip: TripWorkspace) {
	const checklistDone = trip.checklist.filter((item) => item.done).length;
	const itineraryItems = trip.days.flatMap((day) => day.items);
	const itineraryDone = itineraryItems.filter((item) => item.completed).length;
	const plannedDays = trip.days.filter((day) => day.items.length > 0).length;
	const spentJpy = trip.expenses.reduce(
		(total, item) =>
			total +
			(item.currency === "KRW"
				? trip.krwPer100Jpy
					? Math.round(((item.amountKrw ?? 0) * 100) / trip.krwPer100Jpy)
					: 0
				: item.amountJpy),
		0,
	);
	const plannedJpy = trip.days.length * trip.dailyBudgetJpy * trip.partySize;
	return {
		checklistDone,
		checklistTotal: trip.checklist.length,
		itineraryDone,
		itineraryTotal: itineraryItems.length,
		plannedDays,
		totalDays: trip.days.length,
		spentJpy,
		plannedJpy,
	};
}

const filmPhotoPattern =
	/^data:(image\/(?:jpeg|png|webp));base64,((?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?)$/;

export function isTripFilmPhoto(dataUrl: unknown, mimeType: unknown): boolean {
	if (dataUrl === null) return mimeType === null;
	if (typeof dataUrl !== "string" || dataUrl.length > 4_000_000) return false;
	const match = filmPhotoPattern.exec(dataUrl);
	return Boolean(
		match && match[1] === mimeType && match[2] && match[2].length > 0,
	);
}

function isTripFilmMoment(value: unknown): value is TripFilmMoment {
	if (!value || typeof value !== "object") return false;
	const moment = value as Partial<TripFilmMoment>;
	return (
		validId(moment.id) &&
		(moment.planItemId === null || validId(moment.planItemId)) &&
		validDate(moment.dayDate) &&
		boundedText(moment.placeTitle, 80, true) &&
		boundedText(moment.neighborhood, 60, true) &&
		timestamp(moment.capturedAt) &&
		timestamp(moment.createdAt) &&
		typeof moment.featured === "boolean" &&
		(moment.note === undefined || boundedText(moment.note, 1000)) &&
		isTripFilmPhoto(moment.photoDataUrl, moment.photoMimeType)
	);
}

export function createTripFilmMoment(
	trip: TripWorkspace,
	input: Pick<
		TripFilmMoment,
		"planItemId" | "dayDate" | "capturedAt" | "photoDataUrl" | "photoMimeType"
	> &
		Partial<Pick<TripFilmMoment, "placeTitle" | "neighborhood" | "note">>,
	nowIso: string,
	id: string = crypto.randomUUID(),
): TripFilmMoment {
	const day = trip.days.find((entry) => entry.date === input.dayDate);
	const item = day?.items.find((entry) => entry.id === input.planItemId);
	if (
		!day ||
		(input.planItemId !== null && !item) ||
		(input.planItemId === null &&
			(!input.placeTitle?.trim() || !input.neighborhood?.trim()))
	)
		throw new Error("INVALID_FILM_PLACE");
	if (!isTripFilmPhoto(input.photoDataUrl, input.photoMimeType))
		throw new Error("INVALID_FILM_PHOTO");
	const result: TripFilmMoment = {
		id,
		planItemId: item?.id ?? null,
		dayDate: day.date,
		placeTitle: cleanText(item?.title ?? input.placeTitle ?? "", 80),
		neighborhood: cleanText(
			item?.area || input.neighborhood || trip.destinationCity,
			60,
		),
		capturedAt: input.capturedAt,
		photoDataUrl: input.photoDataUrl,
		photoMimeType: input.photoMimeType,
		featured: !(trip.filmMoments ?? []).some(
			(moment) => moment.dayDate === day.date && moment.featured,
		),
		createdAt: nowIso,
		...(input.note !== undefined ? { note: cleanText(input.note, 1000) } : {}),
	};
	if (!isTripFilmMoment(result)) throw new Error("INVALID_FILM_MOMENT");
	return result;
}

export function tripFilmTimeline(trip: TripWorkspace) {
	const order = new Map(
		trip.days.map((day, dayIndex) => [
			day.date,
			new Map(
				day.items.map((item, itemIndex) => [
					item.id,
					itemIndex + dayIndex * 1_000,
				]),
			),
		]),
	);
	return [...(trip.filmMoments ?? [])].sort(
		(a, b) =>
			a.dayDate.localeCompare(b.dayDate) ||
			(order.get(a.dayDate)?.get(a.planItemId ?? "") ??
				Number.MAX_SAFE_INTEGER) -
				(order.get(b.dayDate)?.get(b.planItemId ?? "") ??
					Number.MAX_SAFE_INTEGER) ||
			a.capturedAt.localeCompare(b.capturedAt) ||
			a.createdAt.localeCompare(b.createdAt),
	);
}

export function featureTripFilmMoment(
	moments: TripFilmMoment[],
	momentId: string,
) {
	const target = moments.find((moment) => moment.id === momentId);
	if (!target) return moments;
	return moments.map((moment) => ({
		...moment,
		featured:
			moment.dayDate === target.dayDate
				? moment.id === momentId
				: moment.featured,
	}));
}

export function personalFareSummary(notes: PersonalFareNote[]) {
	const values = notes
		.map((note) => note.amountKrw)
		.filter((amount) => Number.isFinite(amount) && amount > 0)
		.sort((a, b) => a - b);
	if (!values.length)
		return {
			state: "empty" as const,
			n: 0,
			minKrw: null,
			medianKrw: null,
			maxKrw: null,
		};
	const middle = Math.floor(values.length / 2);
	const median =
		values.length % 2
			? values[middle]!
			: Math.round(((values[middle - 1] ?? 0) + (values[middle] ?? 0)) / 2);
	return {
		state: "personal_notes" as const,
		n: values.length,
		minKrw: values[0]!,
		medianKrw: median,
		maxKrw: values.at(-1)!,
	};
}

export function jpyToKrw(amountJpy: number, krwPer100Jpy: number | null) {
	return krwPer100Jpy == null
		? null
		: Math.round((amountJpy * krwPer100Jpy) / 100);
}

const object = (value: unknown): value is Record<string, unknown> =>
	value !== null && typeof value === "object" && !Array.isArray(value);
const boundedText = (
	value: unknown,
	limit: number,
	required = false,
): value is string =>
	typeof value === "string" &&
	value.length <= limit &&
	(!required || value.trim().length > 0) &&
	Array.from(value).every((char) => {
		const code = char.charCodeAt(0);
		return code >= 32 || code === 9 || code === 10 || code === 13;
	});
const validId = (value: unknown): value is string =>
	boundedText(value, 200, true);
const timestamp = (value: unknown): value is string =>
	typeof value === "string" &&
	value.length <= 40 &&
	!Number.isNaN(Date.parse(value)) &&
	/^\d{4}-\d{2}-\d{2}T/.test(value) &&
	validDate(value.slice(0, 10));
const validDate = (value: unknown): value is string => {
	if (typeof value !== "string" || !isoDatePattern.test(value)) return false;
	const date = new Date(`${value}T00:00:00Z`);
	return (
		Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value
	);
};
const optionalText = (value: unknown, limit: number) =>
	value === undefined || boundedText(value, limit);
const nullableText = (value: unknown, limit: number) =>
	value === null || boundedText(value, limit);
const optionalFlag = (value: unknown) =>
	value === undefined || typeof value === "boolean";
const safeUrl = (value: unknown) => {
	if (typeof value !== "string" || value.length > 2000) return false;
	try {
		const url = new URL(value);
		return (
			["https:", "http:"].includes(url.protocol) &&
			!url.username &&
			!url.password
		);
	} catch {
		return false;
	}
};
const uniqueList = <T>(
	value: unknown,
	validate: (entry: unknown) => entry is T,
	max = 1000,
): value is T[] =>
	Array.isArray(value) &&
	value.length <= max &&
	value.every(validate) &&
	new Set(value.map((entry) => (entry as { id: string }).id)).size ===
		value.length;

export function isTripPlanItem(value: unknown): value is TripPlanItem {
	if (!object(value)) return false;
	return (
		validId(value.id) &&
		["spot", "food", "transport", "memo"].includes(String(value.type)) &&
		boundedText(value.title, 80, true) &&
		boundedText(value.area, 60) &&
		(value.time === null ||
			(typeof value.time === "string" &&
				/^(?:[01]\d|2[0-3]):[0-5]\d$/.test(value.time))) &&
		(value.estimateJpy === null ||
			(typeof value.estimateJpy === "number" &&
				finiteInteger(value.estimateJpy, 0, 1_000_000_000))) &&
		nullableText(value.mapQuery, 500) &&
		nullableText(value.note, 2000) &&
		typeof value.completed === "boolean" &&
		(value.placeId === undefined || validId(value.placeId)) &&
		(value.courseId === undefined || validId(value.courseId)) &&
		(value.role === undefined ||
			["required", "optional", "meal", "rest"].includes(String(value.role))) &&
		(value.setting === undefined ||
			["indoor", "covered", "outdoor", "unknown"].includes(
				String(value.setting),
			)) &&
		optionalText(value.japaneseName, 200) &&
		optionalText(value.selectionReason, 1000) &&
		optionalText(value.returnReason, 1000) &&
		(value.sourceUrl === undefined ||
			value.sourceUrl === null ||
			safeUrl(value.sourceUrl)) &&
		(value.checkedAt === undefined ||
			value.checkedAt === null ||
			validDate(value.checkedAt) ||
			timestamp(value.checkedAt)) &&
		(value.stayMinutes === undefined ||
			(typeof value.stayMinutes === "number" &&
				finiteInteger(value.stayMinutes, 0, 1440))) &&
		optionalFlag(value.locked) &&
		optionalFlag(value.deferred)
	);
}

const isChecklistItem = (value: unknown): value is TripChecklistItem =>
	object(value) &&
	validId(value.id) &&
	boundedText(value.title, 120, true) &&
	["entry", "money", "connectivity", "route", "personal"].includes(
		String(value.group),
	) &&
	typeof value.done === "boolean" &&
	(value.officialUrl === null || safeUrl(value.officialUrl)) &&
	(value.assignee === undefined || validId(value.assignee)) &&
	(value.dueOffsetDays === undefined ||
		(typeof value.dueOffsetDays === "number" &&
			finiteInteger(value.dueOffsetDays, 0, 365)));
const isExpense = (value: unknown): value is TripExpense =>
	object(value) &&
	validId(value.id) &&
	boundedText(value.title, 80, true) &&
	["transport", "food", "stay", "shopping", "experience", "other"].includes(
		String(value.category),
	) &&
	typeof value.amountJpy === "number" &&
	finiteInteger(value.amountJpy, 1, 1_000_000_000) &&
	validDate(value.occurredOn) &&
	(value.paidBy === undefined || validId(value.paidBy)) &&
	(value.splitWith === undefined ||
		(Array.isArray(value.splitWith) &&
			value.splitWith.length > 0 &&
			value.splitWith.length <= 12 &&
			value.splitWith.every(validId) &&
			new Set(value.splitWith).size === value.splitWith.length)) &&
	(value.currency === undefined ||
		["JPY", "KRW"].includes(String(value.currency))) &&
	(value.amountKrw === undefined ||
		(typeof value.amountKrw === "number" &&
			finiteInteger(value.amountKrw, 1, 1_000_000_000))) &&
	(value.currency !== "KRW" || typeof value.amountKrw === "number");
const isFareNote = (value: unknown): value is PersonalFareNote =>
	object(value) &&
	validId(value.id) &&
	typeof value.amountKrw === "number" &&
	finiteInteger(value.amountKrw, 1, 1_000_000_000) &&
	validDate(value.departureDate) &&
	validDate(value.observedOn) &&
	boundedText(value.seller, 60) &&
	boundedText(value.baggage, 60);
const isCompanionNote = (value: unknown): value is TripCompanionNote =>
	object(value) &&
	validId(value.id) &&
	boundedText(value.label, 80, true) &&
	boundedText(value.mustVisit, 500) &&
	boundedText(value.meetingNote, 1000);
const canonicalValue = (value: unknown): unknown =>
	Array.isArray(value)
		? value.map(canonicalValue)
		: object(value)
			? Object.fromEntries(
					Object.keys(value)
						.sort()
						.filter((key) => value[key] !== undefined)
						.map((key) => [key, canonicalValue(value[key])]),
				)
			: value;
const itemFingerprint = (items: TripPlanItem[]) =>
	JSON.stringify(canonicalValue(items));
const adjustmentCondition = (
	value: unknown,
): value is TripAdjustmentCondition =>
	["rain", "tired", "short_time"].includes(String(value));

function isAdjustment(value: unknown): value is TripDayAdjustment {
	if (
		!object(value) ||
		!validId(value.tripId) ||
		!validDate(value.date) ||
		!adjustmentCondition(value.condition) ||
		!timestamp(value.appliedAt) ||
		!uniqueList(value.beforeItems, isTripPlanItem, 500) ||
		!uniqueList(value.afterItems, isTripPlanItem, 500) ||
		typeof value.baseFingerprint !== "string" ||
		typeof value.afterFingerprint !== "string" ||
		value.baseFingerprint !== itemFingerprint(value.beforeItems) ||
		value.afterFingerprint !== itemFingerprint(value.afterItems) ||
		!Array.isArray(value.changes) ||
		value.changes.length > 500
	)
		return false;
	const expected = adjustedItems(value.beforeItems, value.condition);
	return (
		itemFingerprint(value.afterItems) ===
			itemFingerprint(expected.afterItems) &&
		JSON.stringify(canonicalValue(value.changes)) ===
			JSON.stringify(canonicalValue(expected.changes))
	);
}

function replacementItems(
	beforeItems: TripPlanItem[],
	sourceItemId: string,
	replacementItem: TripPlanItem,
) {
	const source = beforeItems.find((item) => item.id === sourceItemId);
	if (
		!source ||
		source.locked ||
		source.time !== null ||
		source.completed ||
		source.deferred
	)
		throw new Error("INVALID_REPLACEMENT_SOURCE");
	if (
		beforeItems.some(
			(item) =>
				item.id === replacementItem.id ||
				(replacementItem.placeId &&
					(item.placeId === replacementItem.placeId ||
						(!item.placeId && item.id === replacementItem.placeId))),
		)
	)
		throw new Error("DUPLICATE_REPLACEMENT_PLACE");
	return beforeItems.flatMap((item) =>
		item.id === sourceItemId
			? [{ ...replacementItem }, { ...item, deferred: true }]
			: [{ ...item }],
	);
}

function isReplacementPreview(
	value: unknown,
): value is TripStopReplacementPreview {
	if (
		!object(value) ||
		!validId(value.tripId) ||
		!validDate(value.date) ||
		!validId(value.sourceItemId) ||
		!uniqueList(value.beforeItems, isTripPlanItem, 500) ||
		!uniqueList(value.afterItems, isTripPlanItem, 500) ||
		!isTripPlanItem(value.replacementItem) ||
		value.replacementItem.completed ||
		value.replacementItem.locked ||
		value.replacementItem.deferred ||
		value.replacementItem.time !== null ||
		value.baseFingerprint !== itemFingerprint(value.beforeItems)
	)
		return false;
	try {
		return (
			itemFingerprint(value.afterItems) ===
			itemFingerprint(
				replacementItems(
					value.beforeItems,
					value.sourceItemId,
					value.replacementItem,
				),
			)
		);
	} catch {
		return false;
	}
}

function isReplacement(value: unknown): value is TripStopReplacement {
	return (
		object(value) &&
		isReplacementPreview(value) &&
		"afterFingerprint" in value &&
		value.afterFingerprint === itemFingerprint(value.afterItems) &&
		"appliedAt" in value &&
		timestamp(value.appliedAt)
	);
}

export function isTripWorkspace(value: unknown): value is TripWorkspace {
	if (!object(value)) return false;
	const trip = value;
	try {
		if (
			trip.schemaVersion !== 1 ||
			!validId(trip.id) ||
			!boundedText(trip.title, 80, true) ||
			typeof trip.origin !== "string" ||
			!/^[A-Z]{3}$/.test(trip.origin) ||
			typeof trip.destination !== "string" ||
			!/^[A-Z]{3}$/.test(trip.destination) ||
			!boundedText(trip.destinationCity, 40, true) ||
			!validDate(trip.startDate) ||
			!validDate(trip.endDate) ||
			typeof trip.partySize !== "number" ||
			!finiteInteger(trip.partySize, 1, 12) ||
			typeof trip.dailyBudgetJpy !== "number" ||
			!finiteInteger(trip.dailyBudgetJpy, 0, 1_000_000) ||
			!(
				trip.krwPer100Jpy === null ||
				(typeof trip.krwPer100Jpy === "number" &&
					finiteInteger(trip.krwPer100Jpy, 100, 10_000))
			) ||
			(trip.stageOverride !== null && trip.stageOverride !== "archived") ||
			!timestamp(trip.createdAt) ||
			!timestamp(trip.updatedAt) ||
			!Array.isArray(trip.styles) ||
			trip.styles.length > 6 ||
			new Set(trip.styles).size !== trip.styles.length ||
			!trip.styles.every((style) =>
				["food", "culture", "nature", "shopping", "family", "slow"].includes(
					style,
				),
			)
		)
			return false;
		if (
			trip.party !== undefined &&
			(!uniqueList(
				trip.party,
				(p): p is { id: string; nickname: string } =>
					object(p) && validId(p.id) && boundedText(p.nickname, 10, true),
				12,
			) ||
				trip.party.length > trip.partySize)
		)
			return false;
		if (
			trip.companionType !== undefined &&
			!["baby", "parents", "friends", "other"].includes(
				String(trip.companionType),
			)
		)
			return false;
		if (
			trip.stay !== undefined &&
			(!object(trip.stay) ||
				!boundedText(trip.stay.name, 80) ||
				!boundedText(trip.stay.addressJa, 300) ||
				!boundedText(trip.stay.note, 500))
		)
			return false;
		if (
			trip.returnTransport !== undefined &&
			(!object(trip.returnTransport) ||
				![trip.returnTransport.minutes, trip.returnTransport.buffer].every(
					(v) => typeof v === "number" && finiteInteger(v, 0, 1440),
				))
		)
			return false;
		if (trip.calendarAddedAt !== undefined && !timestamp(trip.calendarAddedAt))
			return false;
		if (trip.shareId !== undefined && !validId(trip.shareId)) return false;
		if (
			trip.shareRevision !== undefined &&
			!(
				typeof trip.shareRevision === "number" &&
				finiteInteger(trip.shareRevision, 0, 1_000_000)
			)
		)
			return false;
		if (
			trip.sharedFrom !== undefined &&
			(!object(trip.sharedFrom) ||
				!validId(trip.sharedFrom.shareId) ||
				typeof trip.sharedFrom.revision !== "number" ||
				!finiteInteger(trip.sharedFrom.revision, 1, 1_000_000) ||
				!timestamp(trip.sharedFrom.receivedAt))
		)
			return false;
		if (
			trip.votes !== undefined &&
			(!object(trip.votes) ||
				Object.keys(trip.votes).length > 1000 ||
				!Object.entries(trip.votes).every(
					([id, rows]) =>
						validId(id) &&
						Array.isArray(rows) &&
						rows.length <= 12 &&
						rows.every(
							(v) =>
								object(v) &&
								boundedText(v.nick, 10, true) &&
								["yes", "skip"].includes(String(v.value)) &&
								timestamp(v.at),
						),
				))
		)
			return false;
		if (
			trip.settlements !== undefined &&
			(!Array.isArray(trip.settlements) ||
				trip.settlements.length > 12 ||
				!trip.settlements.every(
					(v) =>
						object(v) &&
						validId(v.from) &&
						validId(v.to) &&
						typeof v.amountJpy === "number" &&
						finiteInteger(v.amountJpy, 0, 1_000_000_000) &&
						timestamp(v.doneAt),
				))
		)
			return false;
		if (
			trip.bookTheme !== undefined &&
			!["navy", "red", "forest"].includes(String(trip.bookTheme))
		)
			return false;
		const dates = dateRange(trip.startDate, trip.endDate);
		if (
			trip.travelContext !== undefined &&
			(!object(trip.travelContext) ||
				!boundedText(trip.travelContext.stayArea, 80) ||
				![
					trip.travelContext.arrivalTime,
					trip.travelContext.departureTime,
				].every(
					(time) =>
						time === null ||
						(typeof time === "string" &&
							/^(?:[01]\d|2[0-3]):[0-5]\d$/.test(time)),
				))
		)
			return false;
		if (
			!Array.isArray(trip.days) ||
			trip.days.length !== dates.length ||
			!trip.days.every(
				(day, index) =>
					object(day) &&
					day.date === dates[index] &&
					uniqueList(day.items, isTripPlanItem, 500),
			) ||
			!uniqueList(trip.checklist, isChecklistItem) ||
			!uniqueList(trip.expenses, isExpense, MAX_TRIP_EXPENSES) ||
			!uniqueList(trip.fareNotes, isFareNote) ||
			(trip.candidates !== undefined &&
				!uniqueList(trip.candidates, isTripPlanItem, 1000)) ||
			(trip.companionNotes !== undefined &&
				!uniqueList(trip.companionNotes, isCompanionNote, 24)) ||
			(trip.filmMoments !== undefined &&
				!uniqueList(trip.filmMoments, isTripFilmMoment, 500))
		)
			return false;
		const days = trip.days as TripDay[];
		if (
			Array.isArray(trip.filmMoments) &&
			!trip.filmMoments.every((moment: TripFilmMoment) =>
				days.some(
					(day) =>
						day.date === moment.dayDate &&
						(moment.planItemId === null ||
							day.items.some((item) => item.id === moment.planItemId)),
				),
			)
		)
			return false;
		if (
			trip.lastAdjustment !== undefined &&
			trip.lastAdjustment !== null &&
			(!isAdjustment(trip.lastAdjustment) ||
				trip.lastAdjustment.tripId !== trip.id ||
				!dates.includes(trip.lastAdjustment.date))
		)
			return false;
		if (
			trip.lastReplacement !== undefined &&
			trip.lastReplacement !== null &&
			(!isReplacement(trip.lastReplacement) ||
				trip.lastReplacement.tripId !== trip.id ||
				!dates.includes(trip.lastReplacement.date))
		)
			return false;
		return true;
	} catch {
		return false;
	}
}

function ensureTrip(trip: TripWorkspace) {
	if (!isTripWorkspace(trip)) throw new Error("INVALID_TRIP_WORKSPACE");
}

export function appendTripStops(
	trip: TripWorkspace,
	date: string,
	stops: TripStopInput[],
	nowIso = new Date().toISOString(),
): TripWorkspace {
	ensureTrip(trip);
	const copy = structuredClone(trip);
	const day = copy.days.find((entry) => entry.date === date);
	if (!day) throw new Error("INVALID_TRIP_DAY");
	const originalCount = day.items.length;
	for (const stop of stops) {
		const placeId = stop.placeId ?? stop.id;
		if (
			placeId &&
			day.items.some(
				(item) =>
					item.placeId === placeId ||
					(!item.placeId &&
						(item.id === placeId ||
							(stop.id !== undefined && item.id === stop.id))),
			)
		)
			continue;
		const item: TripPlanItem = {
			...stop,
			id: crypto.randomUUID(),
			completed: false,
			...(placeId ? { placeId } : {}),
		};
		if (!isTripPlanItem(item)) throw new Error("INVALID_TRIP_STOP");
		day.items.push(item);
	}
	if (day.items.length !== originalCount) copy.updatedAt = nowIso;
	ensureTrip(copy);
	return copy;
}

const adjustmentReasons: Record<TripAdjustmentCondition, string> = {
	rain: "비 오는 날 선택 방문을 덜었어요. 실내 대안과 영업 여부는 공식 안내에서 확인해 주세요.",
	tired: "잠근 일정과 식사·쉼을 남기고 선택 방문을 미뤘어요.",
	short_time:
		"시간이 짧아져 선택 방문을 미뤘어요. 이동과 귀환 가능 시간을 보장하지 않아요.",
};

function adjustedItems(
	beforeItems: TripPlanItem[],
	condition: TripAdjustmentCondition,
) {
	const changes: TripDayAdjustmentPreview["changes"] = [];
	const eligible = beforeItems.filter(
		(item) =>
			item.role === "optional" &&
			!item.completed &&
			!item.locked &&
			item.time === null &&
			!item.deferred,
	);
	const keepCount =
		condition === "tired"
			? Math.min(1, Math.max(0, eligible.length - 1))
			: Math.floor(eligible.length / 2);
	const afterItems = beforeItems.map((item) => {
		if (!eligible.includes(item)) return { ...item };
		if (
			condition === "rain" &&
			(item.setting === "indoor" || item.setting === "covered")
		)
			return { ...item };
		if (condition !== "rain" && eligible.indexOf(item) < keepCount)
			return { ...item };
		changes.push({
			itemId: item.id,
			title: item.title,
			reason: adjustmentReasons[condition],
		});
		return { ...item, deferred: true };
	});
	return { afterItems, changes };
}

/** An explicit edit preserves item identity and every attached film moment. */
export function moveTripPlanItem(
	trip: TripWorkspace,
	date: string,
	itemId: string,
	targetDate: string,
	offset = 0,
	nowIso = new Date().toISOString(),
): TripWorkspace {
	ensureTrip(trip);
	if (!Number.isInteger(offset) || Math.abs(offset) > 1)
		throw new Error("INVALID_ITEM_MOVE");
	const copy = structuredClone(trip);
	const from = copy.days.find((day) => day.date === date),
		target = copy.days.find((day) => day.date === targetDate);
	const index = from?.items.findIndex((item) => item.id === itemId) ?? -1;
	if (!from || !target || index < 0) throw new Error("INVALID_ITEM_MOVE");
	const item = from.items[index]!;
	if (item.locked || item.time || item.completed)
		throw new Error("PROTECTED_TRIP_ITEM");
	if (from === target) {
		const destination = index + offset;
		if (destination < 0 || destination >= from.items.length) return copy;
		const adjacent = from.items[destination]!;
		if (adjacent.locked || adjacent.time || adjacent.completed)
			throw new Error("PROTECTED_TRIP_ITEM");
		[from.items[index], from.items[destination]] = [adjacent, item];
	} else {
		if (target.items.some((other) => other.id === item.id))
			throw new Error("ITEM_ID_CONFLICT");
		if (
			item.placeId &&
			target.items.some((other) => other.placeId === item.placeId)
		)
			throw new Error("PLACE_ID_CONFLICT");
		from.items.splice(index, 1);
		target.items.push(item);
		copy.filmMoments = copy.filmMoments?.map((moment) =>
			moment.dayDate === date && moment.planItemId === itemId
				? { ...moment, dayDate: targetDate }
				: moment,
		);
		// Previews describe old day arrays and must not be offered after an explicit edit.
		copy.lastAdjustment = null;
		copy.lastReplacement = null;
	}
	copy.lastAdjustment = null;
	copy.lastReplacement = null;
	copy.updatedAt = nowIso;
	ensureTrip(copy);
	return copy;
}

export function previewDayAdjustment(
	trip: TripWorkspace,
	date: string,
	condition: TripAdjustmentCondition,
): TripDayAdjustmentPreview {
	ensureTrip(trip);
	if (!adjustmentCondition(condition))
		throw new Error("INVALID_ADJUSTMENT_CONDITION");
	const day = trip.days.find((entry) => entry.date === date);
	if (!day) throw new Error("INVALID_TRIP_DAY");
	const beforeItems = structuredClone(day.items);
	const { afterItems, changes } = adjustedItems(beforeItems, condition);
	return {
		tripId: trip.id,
		date,
		condition,
		beforeItems,
		afterItems,
		changes,
		baseFingerprint: itemFingerprint(beforeItems),
	};
}

export function applyDayAdjustment(
	trip: TripWorkspace,
	preview: TripDayAdjustmentPreview,
	nowIso = new Date().toISOString(),
): TripWorkspace {
	ensureTrip(trip);
	const day = trip.days.find((entry) => entry.date === preview.date);
	if (
		preview.tripId !== trip.id ||
		!day ||
		itemFingerprint(day.items) !== preview.baseFingerprint
	)
		throw new Error("STALE_DAY_ADJUSTMENT");
	const expected = previewDayAdjustment(trip, preview.date, preview.condition);
	if (
		JSON.stringify(canonicalValue(expected)) !==
		JSON.stringify(canonicalValue(preview))
	)
		throw new Error("INVALID_DAY_ADJUSTMENT");
	if (!preview.changes.length) return structuredClone(trip);
	const copy = structuredClone(trip);
	copy.days.find((entry) => entry.date === preview.date)!.items =
		structuredClone(preview.afterItems);
	copy.lastAdjustment = {
		...structuredClone(preview),
		appliedAt: nowIso,
		afterFingerprint: itemFingerprint(preview.afterItems),
	};
	copy.updatedAt = nowIso;
	ensureTrip(copy);
	return copy;
}

export function undoDayAdjustment(
	trip: TripWorkspace,
	nowIso = new Date().toISOString(),
): TripWorkspace {
	ensureTrip(trip);
	const adjustment = trip.lastAdjustment;
	if (!adjustment) throw new Error("NO_DAY_ADJUSTMENT");
	const day = trip.days.find((entry) => entry.date === adjustment.date);
	if (!day || itemFingerprint(day.items) !== adjustment.afterFingerprint)
		throw new Error("STALE_DAY_ADJUSTMENT");
	const copy = structuredClone(trip);
	copy.days.find((entry) => entry.date === adjustment.date)!.items =
		structuredClone(adjustment.beforeItems);
	copy.lastAdjustment = null;
	copy.updatedAt = nowIso;
	ensureTrip(copy);
	return copy;
}

export function removeTripPlanItem(
	trip: TripWorkspace,
	date: string,
	itemId: string,
	nowIso = new Date().toISOString(),
): TripWorkspace {
	ensureTrip(trip);
	const copy = structuredClone(trip);
	const day = copy.days.find((entry) => entry.date === date);
	if (!day) throw new Error("INVALID_TRIP_DAY");
	const item = day.items.find((entry) => entry.id === itemId);
	if (item?.locked) throw new Error("LOCKED_TRIP_ITEM");
	day.items = day.items.filter((entry) => entry.id !== itemId);
	copy.filmMoments = (copy.filmMoments ?? []).map((moment) =>
		moment.dayDate === date && moment.planItemId === itemId
			? { ...moment, planItemId: null }
			: moment,
	);
	copy.updatedAt = nowIso;
	ensureTrip(copy);
	return copy;
}

/** Build one local preview. The old place remains intact and can be restored on cancel/undo. */
export function previewStopReplacement(
	trip: TripWorkspace,
	date: string,
	itemId: string,
	stop: TripStopInput,
): TripStopReplacementPreview {
	ensureTrip(trip);
	const day = trip.days.find((entry) => entry.date === date);
	if (!day) throw new Error("INVALID_TRIP_DAY");
	const source = day.items.find((item) => item.id === itemId);
	if (
		!source ||
		source.locked ||
		source.time !== null ||
		source.completed ||
		source.deferred
	)
		throw new Error("INVALID_REPLACEMENT_SOURCE");
	const appended = appendTripStops(
		trip,
		date,
		[{ ...stop, time: null, locked: false, deferred: false }],
		trip.updatedAt,
	);
	const afterAppend = appended.days.find((entry) => entry.date === date)!.items;
	if (afterAppend.length === day.items.length)
		throw new Error("DUPLICATE_REPLACEMENT_PLACE");
	const replacementItem = structuredClone(afterAppend.at(-1)!);
	const beforeItems = structuredClone(day.items);
	const afterItems = replacementItems(beforeItems, itemId, replacementItem);
	return {
		tripId: trip.id,
		date,
		sourceItemId: itemId,
		beforeItems,
		afterItems,
		replacementItem,
		baseFingerprint: itemFingerprint(beforeItems),
	};
}

export function applyStopReplacement(
	trip: TripWorkspace,
	preview: TripStopReplacementPreview,
	nowIso = new Date().toISOString(),
): TripWorkspace {
	ensureTrip(trip);
	if (!isReplacementPreview(preview))
		throw new Error("INVALID_STOP_REPLACEMENT");
	const day = trip.days.find((entry) => entry.date === preview.date);
	if (
		preview.tripId !== trip.id ||
		!day ||
		itemFingerprint(day.items) !== preview.baseFingerprint
	)
		throw new Error("STALE_STOP_REPLACEMENT");
	const copy = structuredClone(trip);
	copy.days.find((entry) => entry.date === preview.date)!.items =
		structuredClone(preview.afterItems);
	copy.lastReplacement = {
		...structuredClone(preview),
		afterFingerprint: itemFingerprint(preview.afterItems),
		appliedAt: nowIso,
	};
	copy.updatedAt = nowIso;
	ensureTrip(copy);
	return copy;
}

export function undoStopReplacement(
	trip: TripWorkspace,
	nowIso = new Date().toISOString(),
): TripWorkspace {
	ensureTrip(trip);
	const replacement = trip.lastReplacement;
	if (!replacement) throw new Error("NO_STOP_REPLACEMENT");
	const day = trip.days.find((entry) => entry.date === replacement.date);
	if (!day || itemFingerprint(day.items) !== replacement.afterFingerprint)
		throw new Error("STALE_STOP_REPLACEMENT");
	const copy = structuredClone(trip);
	copy.days.find((entry) => entry.date === replacement.date)!.items =
		structuredClone(replacement.beforeItems);
	// A scene captured at the alternative belongs to the traveller even after the schedule is undone.
	copy.filmMoments = (copy.filmMoments ?? []).map((moment) =>
		moment.dayDate === replacement.date &&
		moment.planItemId === replacement.replacementItem.id
			? { ...moment, planItemId: null }
			: moment,
	);
	copy.lastReplacement = null;
	copy.updatedAt = nowIso;
	ensureTrip(copy);
	return copy;
}

export function cloneTripWorkspace(
	source: TripWorkspace,
	input: Pick<CreateTripInput, "title" | "startDate" | "endDate">,
	nowIso: string,
	id: string = crypto.randomUUID(),
	options: { selection?: "all" | "unvisited_or_return" } = {},
) {
	ensureTrip(source);
	if (
		options.selection !== undefined &&
		!["all", "unvisited_or_return"].includes(options.selection)
	)
		throw new Error("INVALID_CLONE_SELECTION");
	const clone = createTripWorkspace({ ...source, ...input }, nowIso, id);
	const selected = (item: TripPlanItem) =>
		options.selection !== "unvisited_or_return" ||
		!item.completed ||
		Boolean(item.returnReason?.trim());
	const reset = (item: TripPlanItem): TripPlanItem => ({
		...structuredClone(item),
		id: crypto.randomUUID(),
		time: null,
		locked: false,
		completed: false,
		deferred: false,
	});
	clone.candidates = (source.candidates ?? []).filter(selected).map(reset);
	source.days.forEach((day, dayIndex) => {
		const items = day.items.filter(selected).map(reset);
		const target = clone.days[dayIndex];
		if (target) target.items.push(...items);
		else clone.candidates!.push(...items);
	});
	clone.checklist = source.checklist.map((item) => ({ ...item, done: false }));
	ensureTrip(clone);
	return clone;
}
